// src/pages/dashboards/ParentDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  UserGroupIcon,
  AcademicCapIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  BookOpenIcon,
  ArrowRightIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../api/client';
import toast from 'react-hot-toast';
import { FireLoader } from '../../components/Loader';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Helper to compute average from an array of numbers
const average = (arr) => {
  if (!arr || arr.length === 0) return 0;
  return Math.round(arr.reduce((a, b) => a + b, 0) / arr.length);
};

const childLabel = (child) =>
  `${child?.first_name || ''} ${child?.last_name || ''}`.trim() || 'Student';

const safeGet = async (url) => {
  try {
    const response = await apiClient.get(url);
    return response.data?.success ? response.data.data || [] : [];
  } catch (error) {
    console.error(`Failed to load ${url}:`, error);
    return [];
  }
};

export default function ParentDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [stats, setStats] = useState({
    totalChildren: 0,
    averageGrade: 0,
    attendanceRate: 0,
    upcomingEvents: 0,
  });
  const [performanceData, setPerformanceData] = useState([]);
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    fetchParentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchParentData = async () => {
    try {
      setLoading(true);

      // 1. Fetch children
      const childrenRes = await apiClient.get('/students/parent/children');
      if (!childrenRes.data.success) {
        toast.error('Could not fetch your children.');
        setLoading(false);
        return;
      }
      const childrenData = childrenRes.data.data || [];
      setChildren(childrenData);

      if (childrenData.length === 0) {
        setStats({ totalChildren: 0, averageGrade: 0, attendanceRate: 0, upcomingEvents: 0 });
        setPerformanceData([]);
        setActivities([]);
        setLoading(false);
        return;
      }

      // 2. For each child, fetch grades, attendance, subjects
      const childPromises = childrenData.map(async (child) => {
        const [grades, attendance, subjects] = await Promise.all([
          safeGet(`/students/${child.id}/grades`),
          safeGet(`/students/${child.id}/attendance`),
          safeGet(`/students/${child.id}/subjects`),
        ]);

        return {
          ...child,
          chartKey: `${childLabel(child)} (${child.id})`,
          grades,
          attendance,
          subjects,
        };
      });

      const childrenWithData = await Promise.all(childPromises);
      setChildren(childrenWithData);

      // 3. Compute stats
      const total = childrenWithData.length;
      const allGradeAverages = [];
      const allAttendanceRates = [];

      childrenWithData.forEach((child) => {
        // Compute average grade from yearly mean if available, else from term averages
        let avgGrade = 0;
        if (child.grades.length > 0) {
          const yearlyMeans = child.grades.map(g => g.yearly?.mean).filter(v => v !== null && v !== undefined);
          if (yearlyMeans.length > 0) {
            avgGrade = average(yearlyMeans);
          } else {
            // fallback: compute from terms
            const allMeans = [];
            child.grades.forEach(g => {
              ['term_1', 'term_2', 'term_3'].forEach(term => {
                if (g.terms[term]?.mean !== null && g.terms[term]?.mean !== undefined) {
                  allMeans.push(g.terms[term].mean);
                }
              });
            });
            avgGrade = average(allMeans);
          }
        }
        allGradeAverages.push(avgGrade);

        // Attendance rate – fixed: use child.attendance
        const totalSessions = child.attendance.length;
        const presentSessions = child.attendance.filter(a => a.status === 'present').length;
        const attendanceRate = totalSessions > 0 ? Math.round((presentSessions / totalSessions) * 100) : 0;
        allAttendanceRates.push(attendanceRate);
      });

      const overallAvgGrade = average(allGradeAverages);
      const overallAttendance = average(allAttendanceRates);

      setStats({
        totalChildren: total,
        averageGrade: overallAvgGrade,
        attendanceRate: overallAttendance,
        upcomingEvents: 2, // placeholder – could be fetched from events endpoint
      });

      // 4. Build performance chart (term-wise average for each child)
      const termMap = {};
      childrenWithData.forEach((child) => {
        child.grades.forEach((g) => {
          ['term_1', 'term_2', 'term_3'].forEach((termKey) => {
            const termData = g.terms[termKey];
            if (termData && termData.mean !== null && termData.mean !== undefined) {
              if (!termMap[termKey]) termMap[termKey] = {};
              if (!termMap[termKey][child.id]) termMap[termKey][child.id] = [];
              termMap[termKey][child.id].push(termData.mean);
            }
          });
        });
      });

      // Compute average per child per term
      const termAverages = {};
      Object.keys(termMap).forEach((termKey) => {
        termAverages[termKey] = {};
        Object.keys(termMap[termKey]).forEach((childId) => {
          const means = termMap[termKey][childId];
          termAverages[termKey][childId] = average(means);
        });
      });

      // Build chart data
      const chartData = [];
      const termLabels = ['term_1', 'term_2', 'term_3'];
      termLabels.forEach((termKey) => {
        const entry = { term: termKey.replace('term_', 'Term ') };
        childrenWithData.forEach((child) => {
          const avg = termAverages[termKey]?.[child.id] || 0;
          entry[child.chartKey] = avg;
        });
        chartData.push(entry);
      });

      // Add Yearly average
      const yearlyEntry = { term: 'Yearly' };
      childrenWithData.forEach((child) => {
        const yearlyMeans = child.grades.map(g => g.yearly?.mean).filter(v => v !== null && v !== undefined);
        const avg = average(yearlyMeans);
        yearlyEntry[child.chartKey] = avg;
      });
      chartData.push(yearlyEntry);

      setPerformanceData(chartData);

      // 5. Build recent activities from grades and attendance
      const activitiesList = [];
      childrenWithData.forEach((child) => {
        // Recent grade updates
        if (child.grades.length > 0) {
          const firstGrade = child.grades[0];
          if (firstGrade) {
            let termMean = null;
            for (const term of ['term_3', 'term_2', 'term_1']) {
              if (firstGrade.terms[term]?.mean !== null && firstGrade.terms[term]?.mean !== undefined) {
                termMean = firstGrade.terms[term].mean;
                break;
              }
            }
            if (termMean !== null) {
              activitiesList.push({
                id: `grade-${child.id}-${Date.now()}`,
                child: child.first_name,
                action: `scored ${termMean}% in ${firstGrade.subject}`,
                time: new Date().toISOString(),
                type: 'grade',
                icon: ChartBarIcon,
                iconColor: 'text-blue-500',
                bgColor: 'bg-blue-50',
              });
            }
          }
        }

        // Recent attendance – take the last attendance record
        if (child.attendance.length > 0) {
          const last = child.attendance[0];
          const status = last.status === 'present' ? 'present' : 'absent';
          const statusText = status === 'present' ? 'was marked present' : 'was absent';
          activitiesList.push({
            id: `att-${child.id}-${Date.now()}`,
            child: child.first_name,
            action: `${statusText} for ${last.session || 'all'} classes today`,
            time: last.date || new Date().toISOString(),
            type: 'attendance',
            icon: CheckCircleIcon,
            iconColor: status === 'present' ? 'text-green-500' : 'text-red-500',
            bgColor: status === 'present' ? 'bg-green-50' : 'bg-red-50',
          });
        }
      });

      // Sort by time descending and take first 4
      activitiesList.sort((a, b) => new Date(b.time) - new Date(a.time));
      setActivities(activitiesList.slice(0, 4));

    } catch (error) {
      console.error('Error fetching parent data:', error);
      toast.error('Failed to load parent dashboard');
    } finally {
      setLoading(false);
    }
  };

  const timeAgo = (date) => {
    if (!date) return 'Just now';
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return past.toLocaleDateString();
  };

  const fullName = user?.first_name ? `${user.first_name} ${user.last_name || ''}` : 'Parent';

  if (loading) return <FireLoader fullScreen={true} size="lg" text="LOADING..." />;

  return (
    <>
      {/* ========== KEYFRAME ANIMATIONS ========== */}
      <style>{`
        @keyframes wave {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(15deg); }
          50% { transform: rotate(0deg); }
          75% { transform: rotate(-15deg); }
          100% { transform: rotate(0deg); }
        }
        .wave-emoji {
          display: inline-block;
          animation: wave 1.5s ease-in-out infinite;
          transform-origin: 70% 70%;
        }
        .responsive-padding {
          padding-left: 0.75rem;
          padding-right: 0.75rem;
        }
        @media (min-width: 640px) {
          .responsive-padding {
            padding-left: 1.5rem;
            padding-right: 1.5rem;
          }
        }
        @media (min-width: 1024px) {
          .responsive-padding {
            padding-left: 2rem;
            padding-right: 2rem;
          }
        }

        /* Empty state animations */
        @keyframes float {
          0% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(2deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
        .float-icon {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .slide-up {
          animation: slide-up 0.6s ease-out forwards;
        }
        .slide-up-delay-1 { animation-delay: 0.1s; opacity: 0; }
        .slide-up-delay-2 { animation-delay: 0.2s; opacity: 0; }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6 sm:space-y-8 responsive-padding">
        {/* ========== HEADER - gradient banner with waving hand ========== */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-600 to-secondary-600 shadow-lg p-5 sm:p-8 text-white">
          <div className="absolute right-0 top-0 opacity-10">
            <HomeIcon className="w-32 sm:w-48 h-32 sm:h-32" />
          </div>
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold leading-tight">
                Welcome, {fullName}
                <span className="wave-emoji ml-2" style={{ fontSize: 'inherit' }}>👋</span>
              </h1>
              <p className="text-sm sm:text-base text-white/80 mt-1">Stay updated on your children's progress.</p>
            </div>
          </div>
        </div>

        {/* ========== QUICK ACTION TILES ========== */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <div
            onClick={() => navigate('/app/children')}
            className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-3 sm:p-4 text-center cursor-pointer hover:shadow-lg transition-shadow border border-blue-200/50 touch-manipulation"
          >
            <UserGroupIcon className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 mx-auto mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-blue-800">My Children</span>
          </div>
          <div
            onClick={() => navigate('/app/grades')}
            className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-3 sm:p-4 text-center cursor-pointer hover:shadow-lg transition-shadow border border-green-200/50 touch-manipulation"
          >
            <ChartBarIcon className="w-7 h-7 sm:w-8 sm:h-8 text-green-600 mx-auto mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-green-800">Grades</span>
          </div>
          <div
            onClick={() => navigate('/app/attendance')}
            className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-3 sm:p-4 text-center cursor-pointer hover:shadow-lg transition-shadow border border-purple-200/50 touch-manipulation"
          >
            <CalendarDaysIcon className="w-7 h-7 sm:w-8 sm:h-8 text-purple-600 mx-auto mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-purple-800">Attendance</span>
          </div>
          <div
            onClick={() => navigate('/app/timetable')}
            className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-3 sm:p-4 text-center cursor-pointer hover:shadow-lg transition-shadow border border-orange-200/50 touch-manipulation"
          >
            <ClockIcon className="w-7 h-7 sm:w-8 sm:h-8 text-orange-600 mx-auto mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-orange-800">Timetable</span>
          </div>
        </div>

        {/* ========== STATS CARDS ========== */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-blue-200 transition-colors cursor-pointer group" onClick={() => navigate('/app/children')}>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors">
                <UserGroupIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">Children</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">{stats.totalChildren}</p>
              </div>
            </div>
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-50">
              <span className="text-[10px] sm:text-xs text-gray-400">Total enrolled</span>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-green-200 transition-colors cursor-pointer group" onClick={() => navigate('/app/grades')}>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-green-50 text-green-600 group-hover:bg-green-100 transition-colors">
                <AcademicCapIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">Avg. Grade</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">{stats.averageGrade}%</p>
              </div>
            </div>
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-50">
              <span className="text-[10px] sm:text-xs text-gray-400">Across all children</span>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-purple-200 transition-colors cursor-pointer group" onClick={() => navigate('/app/attendance')}>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-purple-50 text-purple-600 group-hover:bg-purple-100 transition-colors">
                <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">Attendance</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">{stats.attendanceRate}%</p>
              </div>
            </div>
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-50">
              <span className="text-[10px] sm:text-xs text-gray-400">Overall rate</span>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-orange-200 transition-colors cursor-pointer group" onClick={() => navigate('/app/events')}>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-orange-50 text-orange-600 group-hover:bg-orange-100 transition-colors">
                <CalendarDaysIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">Upcoming Events</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">{stats.upcomingEvents}</p>
              </div>
            </div>
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-50">
              <span className="text-[10px] sm:text-xs text-gray-400">This week</span>
            </div>
          </div>
        </div>

        {/* ========== PERFORMANCE CHART – with animated gradient bars ========== */}
        <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800">Academic Performance by Child</h3>
            <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-500">
              {children.map((child) => (
                <span key={child.id} className="flex items-center gap-1">
                  <span
                    className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full"
                    style={{ backgroundColor: `hsl(${(child.id * 137) % 360}, 70%, 50%)` }}
                  ></span>
                  {child.first_name}
                </span>
              ))}
            </div>
          </div>

          {performanceData.length > 0 && performanceData.some(d => children.some(c => d[c.chartKey] && d[c.chartKey] > 0)) ? (
            <div className="w-full h-52 sm:h-64 md:h-72 lg:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={performanceData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                  {/* ===== GRADIENT DEFINITIONS ===== */}
                  <defs>
                    {children.map((child) => {
                      const hue = (child.id * 137) % 360;
                      return (
                        <linearGradient
                          key={child.id}
                          id={`gradient-${child.id}`}
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop offset="0%" stopColor={`hsl(${hue}, 80%, 65%)`} stopOpacity={0.9} />
                          <stop offset="100%" stopColor={`hsl(${hue}, 70%, 45%)`} stopOpacity={1} />
                        </linearGradient>
                      );
                    })}
                  </defs>

                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="term" tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <YAxis domain={[0, 100]} tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      borderRadius: '0.5rem',
                      border: '1px solid #e5e7eb',
                      fontSize: '12px',
                    }}
                    formatter={(value) => [`${value}%`, '']}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  {children.map((child) => (
                    <Bar
                      key={child.id}
                      dataKey={child.chartKey}
                      name={childLabel(child)}
                      fill={`url(#gradient-${child.id})`}
                      radius={[4, 4, 0, 0]}
                      animationDuration={1500}
                      animationEasing="ease-in-out"
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            // ========== MODERN ANIMATED EMPTY STATE ==========
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-100/30 rounded-full blur-2xl pulse-glow"></div>
                <ChartBarIcon className="w-20 h-20 sm:w-24 sm:h-24 text-blue-300 float-icon relative z-10" />
              </div>
              <h4 className="mt-6 text-lg sm:text-xl font-semibold text-gray-700 slide-up slide-up-delay-1">
                No Performance Data Yet
              </h4>
              <p className="mt-2 text-sm sm:text-base text-gray-400 max-w-sm slide-up slide-up-delay-2">
                Grades will appear here once teachers start recording assessments for your children.
              </p>
              <button
                onClick={() => navigate('/app/children')}
                className="mt-6 inline-flex items-center px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-xl shadow-md transition-all hover:shadow-lg active:scale-[0.97] touch-manipulation"
              >
                View Children
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </button>
            </div>
          )}

          <div className="mt-2 text-[10px] sm:text-xs text-gray-400 text-center">
            Average grades per term for each child
          </div>
        </div>

        {/* ========== CHILDREN LIST + RECENT ACTIVITY ========== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Children List - takes 1/3 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 h-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">My Children</h3>
                <button
                  onClick={() => navigate('/app/children')}
                  className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-medium touch-manipulation"
                >
                  View All
                </button>
              </div>
              <div className="space-y-3 sm:space-y-4">
                {children.length > 0 ? (
                  children.map((child) => {
                    // compute average grade for this child
                    const yearlyMeans = child.grades?.map(g => g.yearly?.mean).filter(v => v !== null && v !== undefined) || [];
                    const avgGrade = yearlyMeans.length > 0 ? average(yearlyMeans) : '—';
                    return (
                      <div
                        key={child.id}
                        onClick={() => navigate(`/app/students/${child.id}`)}
                        className="flex items-center gap-3 p-2 sm:p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-sm flex-shrink-0">
                          {child.first_name[0]}{child.last_name?.[0] || ''}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800">{child.first_name} {child.last_name}</p>
                          <p className="text-xs text-gray-500">{child.class?.name || 'No class'}</p>
                        </div>
                        <span className="text-xs font-semibold text-primary-600">{avgGrade}%</span>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-400 text-sm">No children linked to your account.</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Activity - takes 2/3 */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 h-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">Recent Activity</h3>
                <button
                  onClick={() => navigate('/app/activities')}
                  className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-medium touch-manipulation"
                >
                  View All
                </button>
              </div>
              <div className="space-y-3">
                {activities.length > 0 ? (
                  activities.map((activity) => (
                    <div key={activity.id} className="flex items-center gap-3 p-2 sm:p-3 rounded-xl hover:bg-gray-50 transition-colors">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${activity.bgColor || 'bg-gray-100'} flex items-center justify-center flex-shrink-0`}>
                        <activity.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${activity.iconColor || 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm text-gray-700 break-words">
                          <span className="font-medium">{activity.child}</span> {activity.action}
                        </p>
                        <p className="text-xs text-gray-400">{timeAgo(activity.time)}</p>
                      </div>
                      <span className={`px-2 py-1 text-[10px] sm:text-xs rounded-full whitespace-nowrap ${
                        activity.type === 'grade' ? 'bg-blue-50 text-blue-600' :
                        activity.type === 'attendance' ? 'bg-green-50 text-green-600' :
                        'bg-gray-50 text-gray-600'
                      } capitalize`}>
                        {activity.type}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <ClockIcon className="w-8 h-8 sm:w-10 sm:h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}